import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './PromoCheckoutDomainHint.module.css';

const TOOLTIP_MARGIN = 16;

type TooltipPosition = { top: number; left: number };

function calcTooltipPosition(btn: HTMLButtonElement, tooltipWidth: number): TooltipPosition {
  const rect = btn.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const top = rect.top - 10;

  const halfW = tooltipWidth / 2;
  const minLeft = TOOLTIP_MARGIN;
  const maxLeft = window.innerWidth - tooltipWidth - TOOLTIP_MARGIN;
  const left = Math.min(Math.max(centerX - halfW, minLeft), maxLeft);

  return { top, left };
}

type PromoCheckoutDomainHintProps = {
  tooltip: string;
  children: string;
};

export function PromoCheckoutDomainHint({ tooltip, children }: PromoCheckoutDomainHintProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<TooltipPosition | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  const tooltipWidth = Math.min(335, window.innerWidth - TOOLTIP_MARGIN * 2);

  const show = useCallback(() => {
    if (btnRef.current) setPos(calcTooltipPosition(btnRef.current, tooltipWidth));
    setOpen(true);
  }, [tooltipWidth]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen((v) => {
      if (!v && btnRef.current) setPos(calcTooltipPosition(btnRef.current, tooltipWidth));
      return !v;
    });
  }, [tooltipWidth]);

  const handlePointerEnter = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.pointerType === 'mouse') show();
    },
    [show],
  );

  const handlePointerLeave = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.pointerType === 'mouse') hide();
    },
    [hide],
  );

  useEffect(() => {
    if (!open) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    const closeOnOutsidePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (btnRef.current?.contains(target)) return;
      if (tooltipRef.current?.contains(target)) return;
      setOpen(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('touchstart', closeOnOutsidePointer);

    return () => {
      window.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('touchstart', closeOnOutsidePointer);
    };
  }, [open]);

  return (
    <div className={styles.wrap}>
      <span className={styles.caption}>{children}</span>
      <span className={styles.triggerWrap}>
        <button
          ref={btnRef}
          type="button"
          className={styles.trigger}
          aria-describedby={open ? id : undefined}
          aria-expanded={open}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onClick={toggle}
        >
          <span className={styles.srOnly}>More info</span>
          <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden>
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <text
              x="12"
              y="16"
              textAnchor="middle"
              fontSize="12"
              fill="currentColor"
              fontWeight="600"
            >
              ?
            </text>
          </svg>
        </button>
        {open && pos != null && createPortal(
          <span
            ref={tooltipRef}
            id={id}
            role="tooltip"
            className={styles.tooltip}
            style={{ top: pos.top, left: pos.left, width: tooltipWidth }}
          >
            {tooltip}
          </span>,
          document.body,
        )}
      </span>
    </div>
  );
}
